import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { dbOperations } from '../../utils/supabase'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  PhotoIcon,
  XMarkIcon,
  CubeIcon
} from '@heroicons/react/24/outline'

const EditProductPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingProduct, setLoadingProduct] = useState(true)
  const [activeTab, setActiveTab] = useState('basic')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    ai_image_url: '', // Geriye uyumluluk için
    ai_images: [], // Yeni çoklu AI görsel sistemi
    model_3d_url: '',
    stock: '',
    is_active: true,
    is_featured: false,
    dimensions: '',
    material: '',
    color: '',
    weight: '',
    brand: '',
    model: '',
    features: '',
    care_instructions: '',
    warranty_months: '24',
    tags: '',
    sku: '',
    barcode: '',
    is_set: false,
    set_items: [],
    profit_margin: '' // Boş = varsayılan %70 kullan
  })

  // Dosya yükleme state'leri
  const [uploadingImages, setUploadingImages] = useState(false)
  const [uploadedImages, setUploadedImages] = useState([])

  // Drag & Drop functions
  const [draggedIndex, setDraggedIndex] = useState(null)

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }

    const newImages = [...uploadedImages]
    const draggedImage = newImages[draggedIndex]
    
    // Remove dragged item
    newImages.splice(draggedIndex, 1)
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage)
    
    setUploadedImages(newImages)
    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Takım içeriği state'i
  const [setItems, setSetItems] = useState([])
  const [newSetItemName, setNewSetItemName] = useState('')
  const [newSetItemQuantity, setNewSetItemQuantity] = useState('1')
  const [newSetItemPrice, setNewSetItemPrice] = useState('')

  // AI ve 3D dosya yönetimi
  const [selectedAIFiles, setSelectedAIFiles] = useState([])
  const [uploadedAIImages, setUploadedAIImages] = useState([])
  const [uploadingAIImages, setUploadingAIImages] = useState(false)
  const [selected3DFiles, setSelected3DFiles] = useState([])
  const [uploaded3DModels, setUploaded3DModels] = useState([])
  const [uploading3DModels, setUploading3DModels] = useState(false)

  useEffect(() => {
    loadData()
    loadProduct()
  }, [id]) // loadProduct fonksiyonu hızla değişmiyorsa bu şekilde bırakılabilir

  const loadData = async () => {
    try {
      const categoriesResponse = await dbOperations.categories.getAll()
      setCategories(categoriesResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const loadProduct = async () => {
    try {
      setLoadingProduct(true)
      const { data: product, error } = await dbOperations.products.getById(id)
      
      if (error || !product) {
        alert('Ürün bulunamadı!')
        navigate('/admin/products')
        return
      }

      // Form verilerini doldur
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.set_items && product.set_items.length > 0 ? '' : product.price?.toString() || '',
        category: product.category || '',
        image_url: product.image_url || '',
        ai_image_url: product.ai_image_url || '', // Geriye uyumluluk
        ai_images: product.ai_images || [], // Yeni çoklu sistem
        model_3d_url: product.model_3d_url || '',
        stock: product.stock?.toString() || '',
        is_active: product.is_active !== false,
        is_featured: product.is_featured || false,
        dimensions: product.dimensions || '',
        material: product.material || '',
        color: product.color || '',
        weight: product.weight?.toString() || '',
        brand: product.brand || '',
        model: product.model || '',
        features: product.features || '',
        care_instructions: product.care_instructions || '',
        warranty_months: product.warranty_months?.toString() || '24',
        tags: product.tags || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        is_set: product.is_set || false,
        set_items: product.set_items || [],
        profit_margin: product.profit_margin ? product.profit_margin.toString() : ''
      })

      // Takım içeriğini doldur (eğer varsa)
      if (product.set_items && Array.isArray(product.set_items)) {
        setSetItems(product.set_items.map((item, index) => ({
          id: item.id || Date.now() + index,
          name: item.name || '',
          quantity: item.quantity || 1,
          price: item.price || 0
        })))
      }

      // Görselleri doldur
      if (product.images && Array.isArray(product.images)) {
        setUploadedImages(product.images.map(img => ({
          url: img.url,
          fileName: img.fileName || null
        })))
      } else if (product.image_url) {
        setUploadedImages([{
          url: product.image_url,
          fileName: null
        }])
      }

      // AI görsellerini doldur
      if (product.ai_images && Array.isArray(product.ai_images)) {
        setUploadedAIImages(product.ai_images.map(img => ({
          url: img.url,
          fileName: img.fileName || null
        })))
      } else if (product.ai_image_url) {
        setUploadedAIImages([{
          url: product.ai_image_url,
          fileName: null
        }])
      }

      // 3D modellerini doldur
      if (product.model_3d_files && Array.isArray(product.model_3d_files)) {
        setUploaded3DModels(product.model_3d_files.map(model => ({
          url: model.url,
          fileName: model.fileName || null
        })))
      } else if (product.model_3d_url) {
        setUploaded3DModels([{
          url: product.model_3d_url,
          fileName: null
        }])
      }

    } catch (error) {
      console.error('Error loading product:', error)
      alert('Ürün yüklenirken hata oluştu!')
      navigate('/admin/products')
    } finally {
      setLoadingProduct(false)
    }
  }

  // Dosya seçme - otomatik upload ile
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    
    if (files.length === 0) return

    setUploadingImages(true)
    try {
      const uploadPromises = files.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          url: result.url,
          fileName: result.fileName
        }))

      const newUploadedImages = [...uploadedImages, ...successfulUploads]
      setUploadedImages(newUploadedImages)

      // Ana resim URL'sini güncelle (ilk yüklenen resim)
      if (successfulUploads.length > 0 && !formData.image_url) {
        const newImageUrl = successfulUploads[0].url
        setFormData(prev => ({
          ...prev,
          image_url: newImageUrl
        }))
      }
    } catch (error) {
      console.error('Error uploading images:', error)
    } finally {
      setUploadingImages(false)
    }
  }

  // Arrow movement functions
  const moveImageUp = (index) => {
    if (index === 0) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index - 1]
    newImages[index - 1] = temp
    setUploadedImages(newImages)
  }

  const moveImageDown = (index) => {
    if (index === uploadedImages.length - 1) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index + 1]
    newImages[index + 1] = temp
    setUploadedImages(newImages)
  }

  // Yüklenmiş resmi kaldırma
  const removeUploadedImage = async (index) => {
    const imageToRemove = uploadedImages[index]
    try {
      // Eğer silinen görsel AI görselleri arasındaysa, AI listesinden de kaldır
      if (imageToRemove) {
        setFormData(prev => {
          const newAIImages = (prev.ai_images || []).filter(img => img.url !== imageToRemove.url);
          return { 
            ...prev, 
            ai_image_url: newAIImages.length > 0 ? newAIImages[0].url : '',
            ai_images: newAIImages
          };
        });
      }
      
      // Supabase'den dosyayı sil
      if (imageToRemove.fileName) {
        await dbOperations.storage.deleteFile(imageToRemove.fileName)
      }
      // State'den kaldır
      setUploadedImages(prev => prev.filter((_, i) => i !== index))
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  // AI görsel olarak seç/kaldır (çoklu seçim)
  const toggleAIImage = (imageUrl) => {
    setFormData(prev => {
      const currentAIImages = prev.ai_images || [];
      const existingIndex = currentAIImages.findIndex(img => img.url === imageUrl);
      
      if (existingIndex >= 0) {
        // Zaten varsa kaldır
        const newAIImages = currentAIImages.filter((_, index) => index !== existingIndex);
        return { 
          ...prev, 
          ai_images: newAIImages,
          ai_image_url: newAIImages.length > 0 ? newAIImages[0].url : '' // İlkini ana yap
        };
      } else {
        // Yoksa ekle
        const newAIImages = [...currentAIImages, { url: imageUrl, is_primary: currentAIImages.length === 0 }];
        return { 
          ...prev, 
          ai_images: newAIImages,
          ai_image_url: newAIImages[0].url // İlkini ana yap
        };
      }
    });
  }

  // AI görselini ana görsel yap
  const setPrimaryAIImage = (imageUrl) => {
    setFormData(prev => {
      const currentAIImages = prev.ai_images || [];
      const updatedAIImages = currentAIImages.map(img => ({
        ...img,
        is_primary: img.url === imageUrl
      }));
      
      return {
        ...prev,
        ai_images: updatedAIImages,
        ai_image_url: imageUrl
      };
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Takım içeriği varsa fiyat kontrolü
      const hasSetItems = setItems.length > 0
      const productPrice = hasSetItems ? 0 : parseFloat(formData.price) || 0

      const productData = {
        name: formData.name,
        description: formData.description,
        price: productPrice,
        category: formData.category,
        image_url: uploadedImages.length > 0 ? uploadedImages[0].url : formData.image_url,
        // Tüm görselleri jsonb array olarak kaydet
        images: uploadedImages.length > 0 ? uploadedImages : (formData.image_url ? [{url: formData.image_url}] : []),
        // AI görselleri - yeni çoklu sistem
        ai_images: formData.ai_images && formData.ai_images.length > 0 ? formData.ai_images : (uploadedAIImages.length > 0 ? uploadedAIImages.map((img, index) => ({ url: img.url, is_primary: index === 0 })) : []),
        // AI görseli - geriye uyumluluk için
        ai_image_url: formData.ai_images && formData.ai_images.length > 0 ? formData.ai_images[0].url : (uploadedAIImages.length > 0 ? uploadedAIImages[0].url : formData.ai_image_url),
        // 3D model - sadece tek URL
        model_3d_url: uploaded3DModels.length > 0 ? uploaded3DModels[0].url : formData.model_3d_url,
        stock: parseInt(formData.stock) || 0,
        is_active: formData.is_active,
        is_featured: formData.is_featured,
        dimensions: formData.dimensions,
        material: formData.material,
        color: formData.color,
        weight: parseFloat(formData.weight) || null,
        brand: formData.brand,
        model: formData.model,
        features: formData.features,
        care_instructions: formData.care_instructions,
        warranty_months: parseInt(formData.warranty_months) || 24,
        tags: formData.tags,
        sku: formData.sku,
        barcode: formData.barcode,
        // Takım alanları - veritabanında varsa kaydet
        is_set: hasSetItems,
        set_items: hasSetItems ? setItems : []
      }

      console.log('Product data being updated:', productData) // Debug için
      const { error } = await dbOperations.products.update(id, productData)
      
      if (error) {
        console.error('Database error details:', error)
        throw error
      }

      alert('Ürün başarıyla güncellendi!')
      navigate('/admin/products')
    } catch (error) {
      console.error('Error updating product:', error)
      console.error('Error details:', error.message, error.details)
      alert(`Ürün güncellenirken hata oluştu: ${error.message || 'Bilinmeyen hata'}`)
    } finally {
      setLoading(false)
    }
  }

  const calculateSetPrice = () => {
    const setItemsTotal = setItems.reduce((total, item) => total + (item.quantity * item.price), 0)
    return setItemsTotal
  }

  const calculateDiscount = () => {
    const totalPrice = calculateSetPrice()
    return Math.round(totalPrice * 0.05 * 100) / 100 // %5 indirim
  }

  const addSetItem = () => {
    if (!newSetItemName.trim()) return

    const newItem = {
      id: Date.now(),
      name: newSetItemName.trim(),
      quantity: parseInt(newSetItemQuantity),
      price: parseFloat(newSetItemPrice) || 0
    }

    setSetItems([...setItems, newItem])
    setNewSetItemName('')
    setNewSetItemQuantity('1')
    setNewSetItemPrice('')
  }

  const removeSetItem = (itemId) => {
    setSetItems(setItems.filter(item => item.id !== itemId))
  }

  const updateSetItemQuantity = (itemId, newQuantity) => {
    setSetItems(setItems.map(item => 
      item.id === itemId 
        ? { ...item, quantity: parseInt(newQuantity) }
        : item
    ))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  // AI görsel fonksiyonları
  const handleAIFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setSelectedAIFiles(prevFiles => [...prevFiles, ...files])
  }

  const removeSelectedAIFile = (index) => {
    setSelectedAIFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }

  const removeUploadedAIImage = async (index) => {
    try {
      const imageToRemove = uploadedAIImages[index]
      if (imageToRemove.fileName) {
        await dbOperations.storage.deleteFile(imageToRemove.fileName)
      }
      setUploadedAIImages(prev => prev.filter((_, i) => i !== index))
    } catch (error) {
      console.error('AI görseli silerken hata:', error)
      alert('AI görseli silinirken bir hata oluştu!')
    }
  }

  const uploadAIImages = async () => {
    if (selectedAIFiles.length === 0) return

    setUploadingAIImages(true)
    try {
      const uploadPromises = selectedAIFiles.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const uploadResults = await Promise.all(uploadPromises)
      
      const newImages = uploadResults.filter(result => !result.error).map(result => ({
        url: result.url,
        fileName: result.fileName
      }))
      
      setUploadedAIImages(prev => [...prev, ...newImages])
      setSelectedAIFiles([])
    } catch (error) {
      console.error('AI görseli yüklerken hata:', error)
      alert('AI görseli yüklenirken bir hata oluştu!')
    } finally {
      setUploadingAIImages(false)
    }
  }

  // 3D model fonksiyonları
  const handle3DFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setSelected3DFiles(prevFiles => [...prevFiles, ...files])
  }

  const removeSelected3DFile = (index) => {
    setSelected3DFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }

  const removeUploaded3DModel = async (index) => {
    try {
      const modelToRemove = uploaded3DModels[index]
      if (modelToRemove.fileName) {
        await dbOperations.storage.deleteFile(modelToRemove.fileName)
      }
      setUploaded3DModels(prev => prev.filter((_, i) => i !== index))
    } catch (error) {
      console.error('3D model silerken hata:', error)
      alert('3D model silinirken bir hata oluştu!')
    }
  }

  const upload3DModels = async () => {
    if (selected3DFiles.length === 0) return

    setUploading3DModels(true)
    try {
      const uploadPromises = selected3DFiles.map(file => 
        dbOperations.storage.upload3DModelComplete(file)
      )
      
      const uploadResults = await Promise.all(uploadPromises)
      
      const newModels = uploadResults.filter(result => !result.error).map(result => ({
        url: result.url,
        fileName: result.fileName
      }))
      
      setUploaded3DModels(prev => [...prev, ...newModels])
      setSelected3DFiles([])
    } catch (error) {
      console.error('3D model yüklerken hata:', error)
      alert('3D model yüklenirken bir hata oluştu!')
    } finally {
      setUploading3DModels(false)
    }
  }

  if (loadingProduct) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ürün Düzenle</h1>
            <p className="mt-1 text-sm text-gray-600">
              {formData.name || 'Ürün bilgilerini düzenleyin'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'basic', name: 'Temel Bilgiler' },
              { id: 'details', name: 'Detay Bilgileri' },
              { id: 'advanced', name: 'Gelişmiş' },
              { id: 'sets', name: 'Takım İçeriği' }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Temel Bilgiler */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Ürün Adı *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Kategori *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Fiyat (₺) {setItems.length > 0 && <span className="text-blue-600">(Takım için otomatik hesaplanır)</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required={setItems.length === 0}
                  disabled={setItems.length > 0}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${setItems.length > 0 ? 'bg-gray-100' : ''}`}
                  placeholder={setItems.length > 0 ? "Takım fiyatı otomatik hesaplanır" : ""}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Stok *</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">SKU (Stok Kodu)</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Durum checkboxları */}
              <div className="md:col-span-2 flex gap-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Aktif</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Anasayfada Öne Çıkar</span>
                </label>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Ürün Görselleri {uploadingImages && <span className="text-indigo-600">(Yükleniyor...)</span>}
                </label>
                
                {/* Dosya Seçimi */}
                <div className="mb-4">
                  <div className="flex items-center justify-center w-full">
                    <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <PhotoIcon className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">
                            {uploadingImages ? 'Yükleniyor...' : 'Resim seçmek için tıklayın'}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB)</p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={uploadingImages}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Yüklenmiş Görseller */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {uploadedImages.map((image, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`relative group border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-move ${draggedIndex === index ? 'opacity-50' : ''}`}
                      >
                        <img
                          src={image.url}
                          alt={`Ürün görseli ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        
                        {/* AI Görsel Badge - Yeni çoklu sistem */}
                        {formData.ai_images && formData.ai_images.some(img => img.url === image.url) && (
                          <div className="absolute top-2 right-2">
                            <div className={`text-white text-xs px-2 py-1 rounded-full font-medium shadow-md ${
                              formData.ai_images.find(img => img.url === image.url)?.is_primary 
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600' 
                                : 'bg-gradient-to-r from-purple-400 to-pink-400'
                            }`}>
                              🤖 AI {formData.ai_images.find(img => img.url === image.url)?.is_primary ? '(Ana)' : ''}
                            </div>
                          </div>
                        )}
                        
                        {/* Ana Görsel Badge */}
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md">
                            ⭐ Ana
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => moveImageUp(index)}
                              disabled={index === 0}
                              className="p-1 bg-white rounded-full hover:bg-gray-100 disabled:opacity-50"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveImageDown(index)}
                              disabled={index === uploadedImages.length - 1}
                              className="p-1 bg-white rounded-full hover:bg-gray-100 disabled:opacity-50"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleAIImage(image.url)}
                              className={`p-1 rounded-full transition-all ${
                                formData.ai_images && formData.ai_images.some(img => img.url === image.url)
                                  ? 'bg-purple-600 text-white' 
                                  : 'bg-purple-100 text-purple-600 hover:bg-purple-600 hover:text-white'
                              }`}
                              title={formData.ai_images && formData.ai_images.some(img => img.url === image.url) ? 'AI Listesinden Kaldır' : 'AI Listesine Ekle'}
                            >
                              🤖
                            </button>
                            <button
                              type="button"
                              onClick={() => removeUploadedImage(index)}
                              className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {index === 0 && (
                          <div className="absolute top-1 left-1 bg-green-600 text-white text-xs px-1 rounded">
                            Ana
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detay Bilgileri Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Boyutlar</label>
                <input
                  type="text"
                  value={formData.dimensions}
                  onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  placeholder="örn: 200x150x80 cm"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Malzeme</label>
                <input
                  type="text"
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  placeholder="örn: Meşe, Kumaş"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Renk</label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="örn: Kahverengi, Beyaz"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Ağırlık (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Marka</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Özellikler</label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  rows={3}
                  placeholder="Ürünün öne çıkan özelliklerini listeleyin"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Bakım Talimatları</label>
                <textarea
                  value={formData.care_instructions}
                  onChange={(e) => setFormData({ ...formData, care_instructions: e.target.value })}
                  rows={3}
                  placeholder="Ürünün bakım ve temizlik talimatları"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Garanti (Ay)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.warranty_months}
                  onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Barkod</label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Kar Oranı (%) 
                  <span className="text-gray-500 text-xs ml-2">Boş bırakılırsa varsayılan %70 uygulanır</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="500"
                  step="0.1"
                  value={formData.profit_margin}
                  onChange={(e) => setFormData({ ...formData, profit_margin: e.target.value })}
                  placeholder="70 (varsayılan)"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Örnek: 50 yazarsanız %50 kar, 100 yazarsanız %100 kar uygulanır
                </p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Etiketler</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="virgülle ayırın: modern, rahat, şık"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          )}

          {/* Gelişmiş Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Gelişmiş Görsel Ayarları</h3>
                <p className="text-sm text-gray-600 mb-6">
                  AI görseli ve 3D model dosyalarını yükleyin.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* AI Görseli */}
                <div>
                  <label className="block text-lg font-medium text-gray-900 mb-2">🤖 AI Görselleri</label>
                  <p className="text-sm text-gray-600 mb-4">Bu görseller "evimde gör" özelliği için kullanılacak. Birden fazla AI görseli ekleyebilirsiniz.</p>
                  
                  {/* Seçilen AI Görselleri Özeti */}
                  {formData.ai_images && formData.ai_images.length > 0 && (
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-purple-800 mb-2">
                        Seçilen AI Görselleri ({formData.ai_images.length})
                      </h4>
                      <div className="space-y-1">
                        {formData.ai_images.map((img, index) => (
                          <div key={index} className="flex items-center justify-between text-xs text-purple-700">
                            <span className="truncate">
                              {img.url.split('/').pop()} {img.is_primary && '(Ana)'}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleAIImage(img.url)}
                              className="text-red-600 hover:text-red-800 ml-2"
                              title="Kaldır"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Dosya Yükleme */}
                  <div className="mb-4">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors bg-purple-50">
                        <PhotoIcon className="mx-auto h-12 w-12 text-purple-400" />
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold text-purple-600">AI görseli seçin</span>
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB)</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAIFileSelect}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>

                  {/* Seçili AI Dosyaları */}
                  {selectedAIFiles.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Seçili AI Görseli</h4>
                        <button
                          type="button"
                          onClick={uploadAIImages}
                          disabled={uploadingAIImages}
                          className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                          {uploadingAIImages ? 'Yükleniyor...' : 'Yükle'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        {selectedAIFiles.map((file, index) => (
                          <div key={index} className="relative">
                            <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center">
                              <PhotoIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSelectedAIFile(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                            <p className="text-xs text-gray-600 mt-1 truncate w-20">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yüklenmiş AI Görselleri */}
                  {uploadedAIImages.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Yüklenmiş AI Görseli</h4>
                      <div className="flex gap-2">
                        {uploadedAIImages.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image.url}
                              alt={`AI görseli ${index + 1}`}
                              className="w-20 h-20 object-cover rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => removeUploadedAIImage(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-1 left-1 bg-purple-600 text-white text-xs px-1 rounded">
                              AI
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manuel AI URL Girişi */}
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700">Manuel AI Görsel URL</label>
                    <input
                      type="url"
                      value={formData.ai_image_url}
                      onChange={(e) => setFormData({ ...formData, ai_image_url: e.target.value })}
                      placeholder="https://example.com/ai-image.jpg"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    />
                    {formData.ai_image_url && !uploadedAIImages.some(img => img.url === formData.ai_image_url) && (
                      <div className="mt-2">
                        <img 
                          src={formData.ai_image_url} 
                          alt="AI URL Preview" 
                          className="h-20 w-20 object-cover rounded-md"
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* 3D Model */}
                <div>
                  <label className="block text-lg font-medium text-gray-900 mb-2">🎲 3D Model</label>
                  <p className="text-sm text-gray-600 mb-4">Ürünün 3D modeli (.glb formatında)</p>
                  
                  {/* 3D Dosya Yükleme */}
                  <div className="mb-4">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors bg-green-50">
                        <CubeIcon className="mx-auto h-12 w-12 text-green-400" />
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold text-green-600">3D model seçin</span>
                          </p>
                          <p className="text-xs text-gray-500">GLB formatı (MAX. 100MB)</p>
                        </div>
                        <input
                          type="file"
                          accept=".glb,.gltf"
                          onChange={handle3DFileSelect}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>

                  {/* Seçili 3D Dosyaları */}
                  {selected3DFiles.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Seçili 3D Model</h4>
                        <button
                          type="button"
                          onClick={upload3DModels}
                          disabled={uploading3DModels}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {uploading3DModels ? 'Yükleniyor...' : 'Yükle'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        {selected3DFiles.map((file, index) => (
                          <div key={index} className="relative">
                            <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center">
                              <CubeIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSelected3DFile(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                            <p className="text-xs text-gray-600 mt-1 truncate w-20">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yüklenmiş 3D Modeller */}
                  {uploaded3DModels.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Yüklenmiş 3D Model</h4>
                      <div className="flex gap-2">
                        {uploaded3DModels.map((model, index) => (
                          <div key={index} className="relative">
                            <div className="w-20 h-20 bg-green-100 rounded border flex items-center justify-center">
                              <CubeIcon className="w-8 h-8 text-green-600" />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeUploaded3DModel(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-1 left-1 bg-green-600 text-white text-xs px-1 rounded">
                              3D
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manuel 3D URL Girişi */}
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700">Manuel 3D Model URL</label>
                    <input
                      type="url"
                      value={formData.model_3d_url}
                      onChange={(e) => setFormData({ ...formData, model_3d_url: e.target.value })}
                      placeholder="https://example.com/model.glb"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                    />
                    {formData.model_3d_url && (
                      <div className="mt-2 p-2 bg-gray-50 rounded">
                        <p className="text-xs text-gray-600">
                          3D Model: {formData.model_3d_url.split('/').pop()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Takım İçeriği */}
          {activeTab === 'sets' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">💎 Takım İçeriği Düzenleme</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700 mb-2">
                    <strong>💡 Fiyat Mantığı:</strong>
                  </p>
                  <ul className="text-sm text-blue-600 space-y-1 ml-4">
                    <li>• Takım içeriği varsa ana fiyat otomatik olarak sıfırlanır</li>
                    <li>• Takım elemanlarının fiyatları toplam fiyatı oluşturur</li>
                    <li>• Sistem otomatik %5 indirim uygular</li>
                    <li>• Müşteri miktarları değiştirebilir</li>
                  </ul>
                </div>
              </div>

              {/* Ürün Ekleme */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  🛋️ Takım Elemanı Ekle
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      value={newSetItemName}
                      onChange={(e) => setNewSetItemName(e.target.value)}
                      placeholder="Ürün adı (örn: 3'lü Koltuk)"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      min="1"
                      value={newSetItemQuantity}
                      onChange={(e) => setNewSetItemQuantity(e.target.value)}
                      placeholder="Adet"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newSetItemPrice}
                      onChange={(e) => setNewSetItemPrice(e.target.value)}
                      placeholder="Fiyat (₺)"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={addSetItem}
                      disabled={!newSetItemName.trim()}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Takım İçeriği Listesi */}
              {setItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Takım İçeriği</h4>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="divide-y divide-gray-200">
                      {setItems.map((item, index) => (
                        <div key={item.id} className="p-4 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">
                              {item.quantity} adet × {formatCurrency(item.price)} = {formatCurrency(item.quantity * item.price)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateSetItemQuantity(item.id, e.target.value)}
                              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                              type="button"
                              onClick={() => removeSetItem(item.id)}
                              className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-full"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Takım Özeti */}
                  <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
                    <h5 className="text-sm font-medium text-indigo-900 mb-3 flex items-center">
                      💰 Takım Fiyat Özeti
                    </h5>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-indigo-700">
                        <span>🛋️ Takım Elemanları ({setItems.length} çeşit)</span>
                        <span className="font-medium">{formatCurrency(setItems.reduce((total, item) => total + (item.quantity * item.price), 0))}</span>
                      </div>
                      <div className="border-t border-indigo-200 pt-2">
                        <div className="flex justify-between text-sm text-indigo-800">
                          <span className="font-medium">Ara Toplam</span>
                          <span className="font-medium">{formatCurrency(calculateSetPrice())}</span>
                        </div>
                        <div className="flex justify-between text-sm text-green-700">
                          <span>🎁 Takım İndirimi (%5)</span>
                          <span className="font-medium">-{formatCurrency(calculateDiscount())}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-indigo-900 border-t border-indigo-300 pt-2 mt-2">
                          <span>🎯 Final Takım Fiyatı</span>
                          <span>{formatCurrency(calculateSetPrice() - calculateDiscount())}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Güncelleniyor...' : 'Ürünü Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditProductPage 